// src/contexts/OrderContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  getDocs,
  getDoc, 
  query, 
  where, 
  orderBy,
  limit,
  setDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from './AuthContext';

const OrderContext = createContext();

export function useOrder() {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error('useOrder must be used within an OrderProvider');
  }
  return context;
}

export function OrderProvider({ children }) {
  const { currentUser } = useAuth();
  
  // Initialize states from localStorage or with defaults
  const [currentOrder, setCurrentOrder] = useState(() => {
    try {
      const savedOrder = localStorage.getItem('order');
      const parsedOrder = savedOrder ? JSON.parse(savedOrder) : {
        package: null,
        genres: [],
        details: null,
        paymentCompleted: false,
        progress: {
          package: false,
          genres: false,
          details: false,
          checkout: false
        }
      };
      
      console.log('Initial Order State:', parsedOrder);
      return parsedOrder;
    } catch (error) {
      console.error('Error parsing saved order:', error);
      return {
        package: null,
        genres: [],
        details: null,
        paymentCompleted: false,
        progress: {
          package: false,
          genres: false,
          details: false,
          checkout: false
        }
      };
    }
  });
  
  const [orders, setOrders] = useState([]); // Track user's orders
  const [loading, setLoading] = useState(false);
  const [lastCreatedOrderId, setLastCreatedOrderId] = useState(null);
  const [guestMode, setGuestMode] = useState(true); // Track if user is in guest mode

  // Save to localStorage whenever order changes
  useEffect(() => {
    try {
      localStorage.setItem('order', JSON.stringify(currentOrder));
      console.log('Order updated in localStorage:', currentOrder);
    } catch (error) {
      console.error('Error saving order to localStorage:', error);
    }
  }, [currentOrder]);

  // Detect user login/logout and update guestMode
  useEffect(() => {
    setGuestMode(!currentUser);
    
    // If user logs in, attempt to sync their draft from localStorage to Firebase
    if (currentUser && currentOrder) {
      syncOrderToFirebase();
    }
    
    // If user is logged in, fetch their orders
    if (currentUser) {
      getUserOrders();
    }
  }, [currentUser]);

  // Update order details during the order process
  const updateOrderDetails = useCallback((step, data) => {
    console.log(`Updating order details for step ${step}:`, data);
    
    setCurrentOrder(prevOrder => {
      const updatedOrder = JSON.parse(JSON.stringify(prevOrder));
      updatedOrder[step] = data;
      
      if (!updatedOrder.progress) {
        updatedOrder.progress = {
          package: false,
          genres: false,
          details: false,
          checkout: false
        };
      }
      
      switch(step) {
        case 'package':
          updatedOrder.progress.package = true;
          break;
        case 'genres':
          updatedOrder.progress.genres = true;
          break;
        case 'details':
          updatedOrder.progress.details = true;
          break;
        default:
          break;
      }
      
      console.log('Updated Order State:', updatedOrder);
      return updatedOrder;
    });
  }, []);

  // Sync current order to Firebase when user authenticates
  async function syncOrderToFirebase() {
    if (!currentUser || !currentOrder) return;
    
    try {
      console.log('Syncing order to Firebase for user', currentUser.uid);
      
      // Store draft order in user's document
      const draftOrderRef = doc(db, 'users', currentUser.uid, 'draftOrders', 'currentDraft');
      
      await setDoc(draftOrderRef, {
        ...currentOrder,
        updatedAt: new Date().toISOString()
      });
      
      console.log('Successfully synced order to Firebase');
    } catch (error) {
      console.error('Error syncing order to Firebase:', error);
    }
  }

  // Try to load draft order from Firebase when user logs in
  async function loadDraftFromFirebase() {
    if (!currentUser) return;
    
    try {
      console.log('Attempting to load draft from Firebase');
      const draftOrderRef = doc(db, 'users', currentUser.uid, 'draftOrders', 'currentDraft');
      const draftSnapshot = await getDoc(draftOrderRef);
      
      if (draftSnapshot.exists()) {
        const draftData = draftSnapshot.data();
        console.log('Found draft in Firebase:', draftData);
        
        // Compare with local draft to determine which is newer
        const localOrder = JSON.parse(localStorage.getItem('order') || '{}');
        
        // If Firebase draft is newer or local draft doesn't exist, use Firebase draft
        if (!localOrder.updatedAt || 
            (draftData.updatedAt && new Date(draftData.updatedAt) > new Date(localOrder.updatedAt))) {
          console.log('Using Firebase draft (newer than local)');
          setCurrentOrder(draftData);
          localStorage.setItem('order', JSON.stringify(draftData));
        } else {
          console.log('Keeping local draft (newer than Firebase)');
          // Sync local to Firebase
          syncOrderToFirebase();
        }
      } else {
        console.log('No draft found in Firebase, keeping local draft');
        // Sync local to Firebase
        syncOrderToFirebase();
      }
    } catch (error) {
      console.error('Error loading draft from Firebase:', error);
    }
  }

  // Update order progress manually
  function updateOrderProgress(step, completed = true) {
    setCurrentOrder(prevOrder => ({
      ...prevOrder,
      progress: {
        ...prevOrder.progress,
        [step]: completed
      }
    }));
  }

  // Clear current order (after completion or cancellation)
  function clearCurrentOrder() {
    console.log('Clearing current order');
    setCurrentOrder({
      package: null,
      genres: [],
      details: null,
      paymentCompleted: false,
      progress: {
        package: false,
        genres: false,
        details: false,
        checkout: false
      }
    });
    localStorage.removeItem('order');
    
    // Also clear from Firebase if user is logged in
    if (currentUser) {
      try {
        const draftOrderRef = doc(db, 'users', currentUser.uid, 'draftOrders', 'currentDraft');
        setDoc(draftOrderRef, { 
          cleared: true,
          clearedAt: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error clearing Firebase draft:', error);
      }
    }
  }

  // Get price based on package
  function getPackagePrice() {
    switch(currentOrder.package) {
      case 'Basic': return 29;
      case 'Premium': return 49;
      case 'Deluxe': return 99;
      default: return 0;
    }
  }

  // Get max genres based on package
  function getMaxGenres() {
    switch(currentOrder.package) {
      case 'Basic': return 1;
      case 'Premium': return 2;
      case 'Deluxe': return 3;
      default: return 0;
    }
  }

  // Check if an order with the given payment ID already exists
  async function checkForExistingOrder(paymentIntentId) {
    if (!paymentIntentId) return null;
    
    try {
      console.log("Checking for existing order with payment ID:", paymentIntentId);
      const existingOrderQuery = query(
        collection(db, "orders"),
        where("paymentIntentId", "==", paymentIntentId)
      );
      
      const querySnapshot = await getDocs(existingOrderQuery);
      
      if (!querySnapshot.empty) {
        const existingOrder = querySnapshot.docs[0];
        console.log("Found existing order:", existingOrder.id);
        return {
          id: existingOrder.id,
          orderNumber: existingOrder.data().orderNumber,
          ...existingOrder.data()
        };
      }
      
      return null;
    } catch (error) {
      console.error("Error checking for existing order:", error);
      return null;
    }
  }
  
  // Check for recently created orders
  async function getRecentlyCreatedOrder() {
    if (!currentUser) return null;
    
    try {
      console.log("Checking for recently created orders");
      const recentOrdersQuery = query(
        collection(db, "orders"),
        where("userId", "==", currentUser.uid),
        orderBy("createdAt", "desc"),
        limit(1)
      );
      
      const querySnapshot = await getDocs(recentOrdersQuery);
      
      if (!querySnapshot.empty) {
        const recentOrder = querySnapshot.docs[0];
        
        // Check if this order was created in the last minute
        const orderTime = new Date(recentOrder.data().createdAt);
        const now = new Date();
        const secondsAgo = (now - orderTime) / 1000;
        
        if (secondsAgo < 60) {
          console.log("Found recently created order:", recentOrder.id);
          return {
            id: recentOrder.id,
            orderNumber: recentOrder.data().orderNumber,
            ...recentOrder.data()
          };
        }
      }
      
      return null;
    } catch (error) {
      console.error("Error checking for recent orders:", error);
      return null;
    }
  }

  // Create a new order in Firestore
  async function createOrder(paymentIntentId, customerInfo = null, orderData = null) {
    try {
      console.log("Starting order creation process with payment ID:", paymentIntentId);
      setLoading(true);
      
      // First, check if we already have an order with this payment ID
      const existingOrder = await checkForExistingOrder(paymentIntentId);
      if (existingOrder) {
        console.log("Found existing order, returning it instead of creating new one");
        return {
          id: existingOrder.id,
          orderNumber: existingOrder.orderNumber
        };
      }
      
      // Validate required inputs
      if (!paymentIntentId) {
        console.error("Missing payment intent ID");
        throw new Error("Invalid payment information");
      }
      
      if (!currentUser) {
        console.error("No user logged in");
        throw new Error("You must be logged in to create an order");
      }
      
      if (!currentOrder.package || !currentOrder.genres || !currentOrder.details) {
        console.error("Order information incomplete", { 
          package: currentOrder.package, 
          genres: currentOrder.genres, 
          details: currentOrder.details 
        });
        throw new Error("Order information is incomplete");
      }
      
      // Generate order number
      const orderNumber = `MM-${Math.floor(1000 + Math.random() * 9000)}`;
      
      // Calculate delivery times
      const earliestDeliveryDate = new Date();
      const latestDeliveryDate = new Date();

      if (currentOrder.details && currentOrder.details.rushDelivery) {
        // Rush delivery: 3-6 hours
        earliestDeliveryDate.setHours(earliestDeliveryDate.getHours() + 3);
        latestDeliveryDate.setHours(latestDeliveryDate.getHours() + 6);
      } else {
        // Standard delivery: 24-48 hours
        earliestDeliveryDate.setHours(earliestDeliveryDate.getHours() + 24);
        latestDeliveryDate.setHours(latestDeliveryDate.getHours() + 48);
      }

      // Calculate revisions based on package
      const maxRevisions = packageAllowsRevisions(currentOrder.package) ? 
        (currentOrder.package === 'Premium' ? 1 : 2) : 0;

      // Extract customer info for the order
      const customer = customerInfo || {
        fullName: '',
        email: currentUser?.email || '',
        phone: '',
        address: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'US'
      };

      // Check if orderData is provided from checkout (for free orders or discount details)
      if (orderData) {
        // Use order data from checkout, but make sure all fields are in the right format for admin dashboard
        console.log("Using provided order data:", orderData);
        
        const finalOrderData = {
          // Basic order information
          orderNumber,
          userId: currentUser.uid,
          package: currentOrder.package,
          genres: currentOrder.genres,
          
          // Add order details directly at top level (for admin dashboard compatibility)
          songStory: currentOrder.details?.songStory || '',
          keyPoints: currentOrder.details?.keyPoints || '',
          additionalNotes: currentOrder.details?.additionalNotes || '',
          occasion: currentOrder.details?.occasion || '',
          recipientName: currentOrder.details?.recipientName || '',
          songTone: currentOrder.details?.songTone || '',
          favoriteArtists: currentOrder.details?.favoriteArtists || '',
          rushDelivery: currentOrder.details?.rushDelivery || false,
          vocalPreference: currentOrder.details?.vocalPreference || 'no-preference',
          
          // Payment and discount info (provided during checkout)
          paymentIntentId: orderData.paymentId || paymentIntentId,
          discountApplied: orderData.discountApplied || false,
          discountCode: orderData.discountCode || null,
          discountAmount: orderData.discountAmount || null,
          discountValue: orderData.discountValue || null,
          originalTotal: orderData.originalTotal || getPackagePrice(),
          finalTotal: orderData.finalTotal || getPackagePrice(),
          
          // Customer information (place directly in the order for admin dashboard)
          fullName: customer.fullName || '',
          email: customer.email || currentUser?.email || '',
          phone: customer.phone || '',
          address: customer.address || '',
          city: customer.city || '',
          state: customer.state || '',
          postalCode: customer.postalCode || '',
          country: customer.country || 'US',
          
          // Also keep customerInfo object for apps that expect it
          customerInfo: customer,
          
          // Order status information
          status: 'Order Received',
          createdAt: new Date().toISOString(),
          estimatedDelivery: latestDeliveryDate.toISOString(),
          songUrl: null,
          revisionsRemaining: maxRevisions,
          revisions: []
        };
        
        console.log("Creating Firestore document with data", JSON.stringify(finalOrderData));
        
        // Create the order document
        let docRef;
        try {
          docRef = await addDoc(collection(db, "orders"), finalOrderData);
          console.log("Document created with ID:", docRef.id);
          
          // Keep track of the last created order ID
          setLastCreatedOrderId(docRef.id);
          
          // Update the document with its own ID
          await updateDoc(doc(db, "orders", docRef.id), { id: docRef.id });
          console.log("Document updated with its own ID");
        } catch (dbError) {
          console.error("Database operation failed:", dbError);
          throw new Error("Failed to save order to database. Please try again.");
        }
        
        // Update orders state after creation
        try {
          const updatedOrders = await getUserOrders();
          setOrders(updatedOrders);
        } catch (getOrdersError) {
          console.error("Failed to refresh orders:", getOrdersError);
          // Non-critical error, don't throw
        }
        
        updateOrderProgress('checkout', true);
        console.log("Order creation completed successfully with ID:", docRef.id, "Order Number:", orderNumber);
        
        // Return both the document ID and order number
        const result = { 
          id: docRef.id, 
          orderNumber: orderNumber 
        };
        
        console.log("Returning order result:", result);
        return result;
      } else {
        // Use original order structure (for standard orders)
        const standardOrderData = {
          orderNumber,
          userId: currentUser.uid,
          package: currentOrder.package,
          genres: currentOrder.genres,
          
          // Spread details at top level for admin dashboard
          songStory: currentOrder.details?.songStory || '',
          keyPoints: currentOrder.details?.keyPoints || '',
          additionalNotes: currentOrder.details?.additionalNotes || '',
          occasion: currentOrder.details?.occasion || '',
          recipientName: currentOrder.details?.recipientName || '',
          songTone: currentOrder.details?.songTone || '',
          favoriteArtists: currentOrder.details?.favoriteArtists || '',
          rushDelivery: currentOrder.details?.rushDelivery || false,
          vocalPreference: currentOrder.details?.vocalPreference || 'no-preference',
          
          // Customer information
          fullName: customer.fullName || '',
          email: customer.email || currentUser?.email || '',
          phone: customer.phone || '',
          address: customer.address || '',
          city: customer.city || '',
          state: customer.state || '',
          postalCode: customer.postalCode || '',
          country: customer.country || 'US',
          
          // Order status information
          status: 'Order Received',
          paymentIntentId,
          createdAt: new Date().toISOString(),
          estimatedDelivery: latestDeliveryDate.toISOString(),
          songUrl: null,
          revisionsRemaining: maxRevisions,
          revisions: []
        };
        
        console.log("Creating Firestore document with data", JSON.stringify(standardOrderData));
        
        // Create the order document
        let docRef;
        try {
          docRef = await addDoc(collection(db, "orders"), standardOrderData);
          console.log("Document created with ID:", docRef.id);
          
          // Keep track of the last created order ID
          setLastCreatedOrderId(docRef.id);
          
          // Update the document with its own ID
          await updateDoc(doc(db, "orders", docRef.id), { id: docRef.id });
          console.log("Document updated with its own ID");
        } catch (dbError) {
          console.error("Database operation failed:", dbError);
          
          // Check if the order was actually created despite the error
          const recentOrder = await getRecentlyCreatedOrder();
          if (recentOrder) {
            console.log("Order was created despite error:", recentOrder.id);
            return {
              id: recentOrder.id,
              orderNumber: recentOrder.orderNumber
            };
          }
          
          throw new Error("Failed to save order to database. Please try again.");
        }
        
        // Update orders state after creation
        try {
          const updatedOrders = await getUserOrders();
          setOrders(updatedOrders);
        } catch (getOrdersError) {
          console.error("Failed to refresh orders:", getOrdersError);
          // Non-critical error, don't throw
        }
        
        updateOrderProgress('checkout', true);
        console.log("Order creation completed successfully with ID:", docRef.id, "Order Number:", orderNumber);
        
        // Return both the document ID and order number
        const result = { 
          id: docRef.id, 
          orderNumber: orderNumber 
        };
        
        console.log("Returning order result:", result);
        return result;
      }
    } catch (error) {
      console.error("Order creation failed:", error);
      
      // Check if an order was created despite the error
      const recentOrder = await getRecentlyCreatedOrder();
      if (recentOrder) {
        console.log("Order was created despite error:", recentOrder.id);
        return {
          id: recentOrder.id,
          orderNumber: recentOrder.orderNumber,
          createdDespiteError: true
        };
      }
      
      // Rethrow with a user-friendly message
      throw new Error(error.message || "Order creation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // Fetch user orders and update local state
  async function getUserOrders() {
    try {
      setLoading(true);
      
      if (!currentUser) return [];
      
      const q = query(
        collection(db, "orders"),
        where("userId", "==", currentUser.uid),
        orderBy("createdAt", "desc")
      );
      
      const querySnapshot = await getDocs(q);
      const orders = [];
      
      querySnapshot.forEach((doc) => {
        const orderData = doc.data();
        
        // Create a comprehensive order object
        const processedOrder = {
          id: doc.id,
          ...orderData,
          // Ensure details exists
          details: orderData.details || {}
        };
        
        // For backwards compatibility, move top-level details to nested details if not already present
        const detailsFields = [
          'songStory', 'keyPoints', 'additionalNotes', 
          'occasion', 'genre', 'style', 'lyricStyle'
        ];
        
        detailsFields.forEach(field => {
          if (orderData[field] && !processedOrder.details[field]) {
            processedOrder.details[field] = orderData[field];
          }
        });
        
        orders.push(processedOrder);
      });
      
      setOrders(orders); // Update local state with processed orders
      return orders;
    } catch (error) {
      console.error("Error fetching orders:", error);
      return [];
    } finally {
      setLoading(false);
    }
  }

  // Upload a song file (for admin use)
  async function uploadSong(orderId, file) {
    try {
      setLoading(true);
      
      const songRef = ref(storage, `songs/${orderId}/${file.name}`);
      await uploadBytes(songRef, file);
      
      const downloadUrl = await getDownloadURL(songRef);
      
      await updateDoc(doc(db, "orders", orderId), {
        songUrl: downloadUrl,
        status: 'Completed',
        deliveredAt: new Date().toISOString()
      });
      
      // Update orders state after song upload
      const updatedOrders = await getUserOrders();
      setOrders(updatedOrders);
      
      return downloadUrl;
    } catch (error) {
      console.error("Error uploading song:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  // Request a revision
  async function requestRevision(orderId, notes) {
    try {
      setLoading(true);
      
      const orderRef = doc(db, "orders", orderId);
      const orderSnap = await getDoc(orderRef);
      
      if (!orderSnap.exists()) throw new Error("Order not found");
      
      const orderData = orderSnap.data();
      
      // Get the correct max revisions based on package type
      const maxRevisions = packageAllowsRevisions(orderData.package) ? 
        (orderData.package === 'Premium' ? 1 : 2) : 0;
      
      // Calculate used revisions from the revisions array
      const revisionsUsed = orderData.revisions?.length || 0;
      
      // Calculate remaining revisions based on package type and used revisions
      const actualRevisionsRemaining = Math.max(0, maxRevisions - revisionsUsed);
      
      // Check if the user actually has revisions remaining
      if (actualRevisionsRemaining <= 0) {
        throw new Error("No revisions remaining");
      }

      if (orderData.status !== 'Completed' && orderData.status !== 'Revision Requested') {
        throw new Error("Order must be completed to request a revision");
      }

      // Calculate the new number of revisions remaining after this request
      const newRevisionsRemaining = actualRevisionsRemaining - 1;

      await updateDoc(orderRef, {
        status: 'Revision Requested',
        revisionsRemaining: newRevisionsRemaining,
        revisions: [
          ...(orderData.revisions || []),
          {
            note: notes,
            status: 'Revision Requested',
            timestamp: new Date().toISOString(),
            revisionNumber: revisionsUsed + 1
          }
        ]
      });

      // Update local orders state with correct revisionsRemaining
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId ? { 
            ...order, 
            status: 'Revision Requested',
            revisionsRemaining: newRevisionsRemaining,
            revisions: [
              ...(order.revisions || []),
              {
                note: notes,
                status: 'Revision Requested',
                timestamp: new Date().toISOString(),
                revisionNumber: revisionsUsed + 1
              }
            ]
          } : order
        )
      );

      return true;
    } catch (error) {
      console.error("Error requesting revision:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  // Request a variation (keeping existing function for reference)
  async function requestVariation(orderId, variationNotes) {
    try {
      setLoading(true);
      
      const orderRef = doc(db, "orders", orderId);
      const orderSnap = await getDoc(orderRef);
      
      if (!orderSnap.exists()) throw new Error("Order not found");
      
      const orderData = orderSnap.data();
      
      if (orderData.variationsRemaining <= 0) throw new Error("No variations remaining");
      
      await updateDoc(orderRef, {
        status: 'Variation Requested',
        variationsRemaining: orderData.variationsRemaining - 1,
        variationNotes,
        variationRequestedAt: new Date().toISOString()
      });
      
      // Update orders state after variation request
      const updatedOrders = await getUserOrders();
      setOrders(updatedOrders);
      
      return true;
    } catch (error) {
      console.error("Error requesting variation:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  const packageAllowsRevisions = (packageType) => {
    const revisionPackages = ['Premium', 'Deluxe'];
    return revisionPackages.includes(packageType);
  };

  const value = {
    currentOrder,
    loading,
    orders,
    lastCreatedOrderId,
    guestMode,
    updateOrderDetails,
    clearCurrentOrder,
    createOrder,
    getUserOrders,
    uploadSong,
    requestRevision,
    requestVariation,
    getPackagePrice,
    getMaxGenres,
    updateOrderProgress,
    getRecentlyCreatedOrder,
    checkForExistingOrder,
    syncOrderToFirebase,
    loadDraftFromFirebase
  };

  return (
    <OrderContext.Provider value={value}>
      {children}
    </OrderContext.Provider>
  );
}

export default OrderContext;